import { ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, IconButtonProps, styled } from '@mui/material';
import { PropsWithChildren } from 'react';
import { CountdownTimer, FormattedNumber } from 'shared/components';
import { useCommoditySummary } from 'shared/queries';


interface IProductSummaryProps {
  isCollapsed: boolean;
  toggleIsCollapsed: () => void;
}
export function ProductsSummary({isCollapsed, toggleIsCollapsed}: IProductSummaryProps) {
  const { summary } = useCommoditySummary();

  if (!summary) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
      }}
    >
      <Collapse
        in={isCollapsed}
        timeout='auto'
        unmountOnExit
        sx={{
          width: '95%',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            fontSize: 14,
          }}
        >
          <MetricsBox header={'Time Until Prices Are Submitted'}>
            <CountdownTimer endTime={summary.submitByTime} />
          </MetricsBox>

          <MetricsBox header={'Prices Not Yet Reviewed'} value={summary.reviewCount?.toString() ?? '0'} />

          <MetricsBox header={'Prices Marked as Modified'} value={summary.modifiedCount?.toString() ?? '0'} />

          <MetricsBox header={'Prices Marked as Accepted'} value={summary.acceptedCount?.toString() ?? '0'} />

          <MetricsBox header={'Prices Marked as Modified or Accepted (%)'}>
            <FormattedNumber
              value={summary.pricesModifiedAccepted ? summary.pricesModifiedAccepted * 100 : 0}
              decimalScale={2}
              suffix={'%'}
            />
          </MetricsBox>

          <MetricsBox header={'Prices Accepted out of Modified (%)'}>
            <FormattedNumber
              value={summary.pricesAcceptedOverModified ? summary.pricesAcceptedOverModified * 100 : 0}
              decimalScale={2}
              suffix={'%'}
            />
          </MetricsBox>

          <MetricsBox header={'Recommended Margin Per Case ($)'}>
            <FormattedNumber value={summary.recommendedMarginPerCase ?? 0} decimalScale={2} prefix={'$'} />
          </MetricsBox>

          <MetricsBox header={'Revised Margin Per Case ($)'}>
            <FormattedNumber value={summary.revisedMarginPerCase ?? 0} decimalScale={2} prefix={'$'} />
          </MetricsBox>

          <MetricsBox header={'Margin Per Case Delta ($)'}>
            <FormattedNumber value={summary.priceMarginDelta ?? 0} decimalScale={2} prefix={'$'} />
          </MetricsBox>
        </Box>
      </Collapse>
      <ExpandMoreContainer
        expand={isCollapsed}
        onClick={toggleIsCollapsed}
        aria-expanded={isCollapsed}
        aria-label='show more'
      >
        <ExpandMore />
      </ExpandMoreContainer>
    </Box>
  );
}

interface MetricsBoxProps {
  header: string;
  value?: string | number;
}

function MetricsBox(props: PropsWithChildren<MetricsBoxProps>) {
  return (
    <Box
      sx={{
        m: 1,
        mx: 2,
        justifyContent: 'center',
        width: 130,
      }}
    >
      <Box>{props.header}</Box>
      <Box
        sx={{
          fontSize: 16,
          fontWeight: 'bold',
          pt: 1,
        }}
      >
        {props.value ? props.value : props.children}
      </Box>
    </Box>
  );
}

interface IExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMoreContainer = styled((props: IExpandMoreProps) => {
  const { expand, ...other } = props;
  return (
    <Box sx={{ width: '5%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <IconButton {...other} />
    </Box>
  );
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));
